import api from '../../api'

// Services
import { EventBus } from '../../services/eventBusService'

// Utilities
import isEmpty from '../../utility/isEmpty'
import Language from '../../utility/languageUtil'
import Logger from '../../utility/loggerUtil'
import Preference from '../../utility/preferenceUtil'
import Wait from '../../utility/waitUtil'

import { BrandEnum, ServiceBrandEnum, KeyConstants, SettingsConstants } from '../../constants'

export default {
  /**
   * Gets the loans for the current customer
   * @param {JSON} context The context object for the module
   * @param {{brand: BrandEnum}} params Params for the loans call (just brand for now)
   */
  async getLoans (context, params) {
    try {
      context.commit('resetLoans')

      const paramBrand = params === undefined ? BrandEnum.None : params.brand
      let serviceBrand = ServiceBrandEnum.titlemax
      for (const key in ServiceBrandEnum) {
        if (ServiceBrandEnum[key] === paramBrand) {
          serviceBrand = key
          break
        }
      }
      const loansResponse = await api.loan.getLoanSummaries(serviceBrand)
      const loans = loansResponse.loans
      const loanReapplyInfo = loansResponse.reapplyInfo

      Logger.info('Got Loan summaries')
      Logger.debug(loans)

      if (loans.length > 1) {
        context.commit('display/setMultipleAccounts', true, { root: true })
      } else {
        context.commit('display/setMultipleAccounts', false, { root: true })
      }

      context.commit('setLoans', loans)
      context.commit('setDashboardReapplyInfo', loanReapplyInfo)
    } catch (e) {
      throw new Error(`${Language.$t('global.get_loans_query_error')}: ${e.message}`)
    }
  },

  async prepareLoan (context, params) {
    try {
      Wait.waitStart('loans/prepareLoan')
      let details = params.loan

      if (isEmpty(details)) {
        const loanResponse = await api.loan.getLoanDetail(params.loanId, params.lmsId).catch(e => {
          Logger.error('Error getting loan details')
          Logger.debug(e)
          return api.loan.getLoanDetail(params.loanId, params.lmsId)
        })

        if (isEmpty(loanResponse)) {
          throw new Error('Error fetching latest loan data.')
        }

        details = loanResponse.loan
      }

      let preventNoSsnPay = false

      if (!isEmpty(details)) {
        // Check if customer or cosigner does not have SSN
        let noAltSsn = false
        if (details.cosignerId) {
          const coSignInfo = await getCosignerDetails(params.$store, details.lmsId, details.loanId)

          details.alternateName = coSignInfo.customerName
          details.alternateLinked = !isEmpty(coSignInfo.registeredProfile)
          if (details.refinanceEligible === true) {
            noAltSsn = isEmpty(coSignInfo) || (!isEmpty(coSignInfo) && isEmpty(coSignInfo.displaySsn))
          }
        }

        if (context.rootState.session.allowNoSsnRefi === false && details.refinanceEligible === true && (isEmpty(context.rootState.customer.displaySsn) || noAltSsn)) {
          details.payable = false
          preventNoSsnPay = true
        }

        if (!isEmpty(details.storeId)) {
          let storeInfo = context.rootGetters['store/getStoreInfo'](details.storeId)
          let brandConfig = context.rootGetters['store/getBrandConfig'](details.storeId)

          if (!isEmpty(storeInfo)) {
            details.storePhoneNumber = storeInfo.phone
            details.brand = brandConfig.brandName
            details.localStoreState = storeInfo.state.value // Used for state specific rules
          } else {
            await getStoreDetails(params.$store, details.storeId)
            storeInfo = context.rootGetters['store/getStoreInfo'](details.storeId)
            brandConfig = context.rootGetters['store/getBrandConfig'](details.storeId)

            if (!isEmpty(storeInfo)) {
              details.storePhoneNumber = storeInfo.phone
              details.brand = brandConfig.brandName
              details.localStoreState = storeInfo.state.value // Used for state specific rules
            }
          }
        }

        context.commit('updateLoan', details)
      }

      return {
        loan: details,
        preventNoSsnPay
      }
    } catch (error) {
      Logger.error('Error preparing loan')
      Logger.debug(error)
      throw error
    } finally {
      Wait.waitEnd('loans/prepareLoan')
    }
  },

  /**
   * Cancels a pending cash advance and refreshes loan data
   * @param {String} loanId - The unique identifier for the loan
   * @param {String} lmsId - The identifier for the LMS that the loan is associated with.
   * @param {Number} transactionId - The unique identifier for the cash advance on the loan
   */
  async cancelCashAdvance (context, params) {
    const loanId = params.loanId
    const lmsId = params.lmsId
    const transactionId = params.transactionId
    try {
      await api.loan.cancelCashAdvance(loanId, lmsId, transactionId)
    } catch (error) {
      Logger.error('Error canceling cash advance')
      Logger.debug(error)
      throw error
    }
  },

  async getApplications (context) {
    try {
      const applications = await api.loan.getLoanApplicationStatus()
        .catch(e => {
          Logger.error('Error on api.loan.getLoanApplicationStatus')
          Logger.debug(e)
          return api.loan.getLoanApplicationStatus()
        })

      EventBus.$emit('setActiveElement') // Once applications are assigned, if one is in progress a modal will show, this attempt to fix an IE 11 modal display bug

      context.commit('updateApplications', applications)
    } catch (e) {
      Logger.error('Error getting applications')
      Logger.debug(e)
      throw e
    }
  },

  async getSettlementStatus (context, loanId) {
    try {
      const settlementStatus = await api.loan.getSettlementStatus(loanId).catch(e => {
        Logger.debug(e)
        return api.loan.getSettlementStatus(loanId)
      })

      if (isEmpty(settlementStatus)) {
        return
      }

      context.commit('setSettlementStatus', Object.assign({ loanId }, settlementStatus.status))
    } catch (e) {
      Logger.error('Error getting settlement offer status')
      Logger.debug(e)
      throw e
    }
  },

  async setSettlementStatus (context, data) {
    try {
      await api.loan.setSettlementStatus(data.loanId, data.docSigned, data.paymentMade)

      context.commit('setSettlementStatus', data)
    } catch (e) {
      Logger.error('Error setting settlement offer status')
      Logger.debug(e)
      throw e
    }
  },

  async getRiskInfo (context, data) {
    try {
      const riskInfo = await api.loan.getRiskInfo(data.isAddon, data.loanId, data.migrateProduct)
      if (!isEmpty(riskInfo)) {
        context.commit('setRateChangeModal', riskInfo.rateChanged)
        context.commit('setRefinanceDenial', riskInfo.refinanceDenied)
        context.commit('payment/setPaymentData', {
          riskCheck: riskInfo.rateChanged || riskInfo.creditLimitChanged
        }, { root: true })

        await context.dispatch('manageCreditLimitChange', {
          riskInfo
        })
      }
    } catch (e) {
      Logger.error('Error getting risk info.')
      Logger.debug(e)
      throw e
    }
  },

  async manageCreditLimitChange (context, data) {
    try {
      if (!isEmpty(data) && data.hasAcknowledged === true) {
        Preference.delete(KeyConstants.creditLimitChange)
      }

      if (!isEmpty(data) && !isEmpty(data.riskInfo) && data.riskInfo.creditLimitChanged === true) {
        await Preference.set(KeyConstants.creditLimitChange, true)
      }

      const hasCreditLimitChanged = await Preference.get(KeyConstants.creditLimitChange, null) || false
      context.commit('setCreditLimitModal', hasCreditLimitChanged)
    } catch (e) {
      Logger.error('Error checking risk changes')
      Logger.debug(e)
      throw e
    }
  }
}

const getCosignerDetails = async (context, lmsId, loanId) => {
  try {
    const cosignerInfo = await context.cache.dispatch('customer/getCoSignerCustomerInfo', { lmsId, loanId }, {
      timeout: SettingsConstants.shortCacheTimeout
    })
    return cosignerInfo
  } catch (e) {
    Logger.error('Error on getCosignerDetails')
    Logger.debug(e)

    context.cache.delete('customer/getCoSignerCustomerInfo', { lmsId, loanId })
    throw e
  }
}

const getStoreDetails = async (context, storeId) => {
  try {
    await context.cache.dispatch('store/getStoreDetails', storeId, {
      timeout: SettingsConstants.longCacheTimeout
    })
  } catch (e) {
    Logger.error('Error on getStoreDetails')
    Logger.debug(e)

    context.cache.delete('store/getStoreDetails', storeId)
    throw e
  }
}
